import { FilterModel, FilterParamEntry } from '@Apps/Search/Src/Models/FilterModel';
import PagingModel from '@Apps/Search/Src/Models/PagingModel';
import * as searchComposables from '@Composables/SearchComposables';
import { FilterSelectionLocationEntry } from '@Types/FilterSelections';


const getLocationFromDefinitions = (id: number) => {

    return searchComposables.getInitialLocationSelectionDetails(id);
};

const urlConvertersDefaultModelBinder = {

    /**
     * parses a list of values (from query string usually) and
     * matches each param into a filter param entry.
     * where (query string) values are given as: is[0].param=1&is[0].value=3&is[1].param=2
     * this will group together based on the array indexer, so all is[0] are
     * bound together in the same object.
     * @param prefix
     * @param params
     */
    parse(prefix: string, params: { key: string, val: string }[]): Array<FilterParamEntry> {

        let result = new Array<FilterParamEntry>();

        // inline hlper function to get the value from the key
        const getParamNameFromKey = (key: string, ident: string): string => {

            return key.substr(ident.length + 1);
        };

        // these all start with "key[xxx]", and then contain paramid, amount or minvalue
        // we need to group them together based on the key, but we dont actually need the key
        // so we only need to check if the prefix/key is the same, and we need to remove the prefix
        // to determine which attribute it is
        let curIdent = "--";
        let curParam: FilterParamEntry | null = null;
        params.forEach((p) => {

            if (p.key.startsWith(curIdent)) {

                // belongs to the same item,
                // parse value and add to curParam
            }
            else {

                // new entry, so determine new curIdent,
                // create new curParam
                // add to results
                curIdent = p.key.substr(0, p.key.indexOf("]") + 1);
                curParam = new FilterParamEntry();
                result.push(curParam);
            }

            // using current curId and curParam, parse id/value
            let paramName = getParamNameFromKey(p.key, curIdent);
            // note that values array will have the paramName formatted as values[0], values[1] etc.
            if (paramName.toLowerCase().startsWith('values')) {
                paramName = 'values';
            }
            // since we cannot be sure of casing, we need to lower compare
            Object.keys(curParam!).forEach(key => {

              

                if (key.toLowerCase() == paramName) {

                    // workaround typescript forced type
                    if (Array.isArray((curParam as any)[key])) {

                        // we currently only  have a values array... which is number
                        (curParam as any)[key].push(+p.val);
                    }
                    else {
                        (curParam as any)[key] = +p.val;
                    }

                }
            });





        });


        return result;
    },


    toIs(index: number, amenityId: number, ...valueId: number[]) {

        //using old style c# modelbinder for now:
        let str = `Is[${index}].ParamId=${amenityId}`;
        for (let i = 0; i < valueId.length; i++) {
            str += `&Is[${index}].Values=${valueId[i]}`;
        }
        return str;
    },



    toAmount(index: number, amenityId: number, amount: number, isMin = true) {

        //using old style c# modelbinder for now:
        return `Amount[${index}].ParamId=${amenityId}&Amount[${index}].${(isMin ? "MinValue" : "MaxValue")}=${amount}`;

    },

    toHas(index: number, ...amenityId: number[]) {

        return amenityId.map((v, i) => `Has[${index + i}].ParamId=${v}`).join('&');

    },

    toHasSearch(index: number, ...amenityId: number[]) {

        return amenityId.map((v, i) => `HasSearch[${index + i}].ParamId=${v}`).join('&');

    },

    join(...params: Array<Array<string>>) {

        return params.filter(s => s.length > 0).flat().join("&");
    }

};


export function toUrlOrderBy(filter: FilterModel): Array<string> {

    const other = new Array<string>();

    if (filter.orderBy !== undefined && filter.orderBy !== "") {
        other.push(`OrderBy=${filter.orderBy}`);
    }

    return other;
}

export function toUrlDate(filter: FilterModel): Array<string> {

    const other = new Array<string>();

    if (filter.date.arrival && filter.date.departure) {

        other.push(`Date.Min=${filter.date.arrival}`);
        other.push(`Date.Max=${filter.date.departure}`);

    }

    return other;
}

export function toUrlCompanions(filter: FilterModel): Array<string> {

    const other = new Array<string>();

    if (filter.companions.numberOfAdults) {

        other.push(`NumberOfPersons=${filter.companions.numberOfAdults}`);
    }


    if (filter.companions.numberOfPets) {

        other.push(`NumberOfPets=${filter.companions.numberOfPets}`);
    }


    if (filter.companions.numberOfBabies) {

        other.push(`NumberOfBabies=${filter.companions.numberOfBabies}`);
    }

    return other;
}


export function toUrl(filter: FilterModel, paging?: PagingModel): string {


    const has = new Array<string>();
    let hasIndex = 0;
    const is = new Array<string>();
    let isIndex = 0;

    const date = [];

    const amount = new Array<string>();
    let amountIndex = 0;

    const hasSearch = new Array<string>();
    let hasSearchIndex = 0;

    if (!filter.accommodationTypeNoPreference && filter.accommodationTypes.length > 0) {

        // only add specific preferences
        is.push(urlConvertersDefaultModelBinder.toIs(isIndex++, searchComposables.getFilterDefinitions().amenityIdAccommodationType, ...filter.accommodationTypes));
    }


    // validate for default.. no need to send default filter..
    if (filter.bedrooms !== 1) {

        amount.push(urlConvertersDefaultModelBinder.toAmount(amountIndex++, searchComposables.getFilterDefinitions().amenityIdBedrooms, filter.bedrooms));
    }
    if (filter.bathrooms !== 1) {

        amount.push(urlConvertersDefaultModelBinder.toAmount(amountIndex++, searchComposables.getFilterDefinitions().amenityIdBathrooms, filter.bathrooms));
    }





    // get all search criteria in one go:
    const searchCriteriaFlattened = filter.criteria.flat();
    if (searchCriteriaFlattened.length > 0) {

        hasSearch.push(urlConvertersDefaultModelBinder.toHasSearch(hasSearchIndex++, ...searchCriteriaFlattened));
    }


    const other = new Array<string>();

    // currently these are added as regular values:
    if (filter.location.hasValue()) {


        filter.location.selection.forEach((selection, index) => {

            var fs = selection.getAsFormValue();
            if (fs !== false) {


                other.push(`Locations[${index}]=${fs}`);

            }
        });


    }

    other.push(...toUrlDate(filter));

    other.push(...toUrlCompanions(filter));

   


    if (paging !== undefined && paging.hasPaging()) {
        // no need to set page and pagesize if value is 0.. 
        other.push(`Page=${paging.page}`);
        other.push(`PageSize=${paging.pageSize}`);

    }

    other.push(...toUrlOrderBy(filter));

    

    const searchQuery = urlConvertersDefaultModelBinder.join(has, is, hasSearch, amount, other);
    return searchQuery;

}


export function fromUrlBase(filter: FilterModel, search: string): FilterModel {

    const params = new URLSearchParams(search.toLowerCase());


    // using default model binder c#
    const allIs = new Array<{ key: string; val: string }>();
    const allHas = new Array<{ key: string; val: string }>();
    const allAmount = new Array<{ key: string; val: string }>();
    const allHasSearch = new Array<{ key: string; val: string }>();

    // get each valid key:
    // has, date, is, hasSearch, amount
    params.forEach((val, key) => {


        // Date:
        // Date.Min
        if (key.startsWith('date.')) {

            if (key === 'date.min') {

                // todo: should check is valid date

                filter.date.arrival = val;
            }
            else if (key === 'date.max') {

                filter.date.departure = val;
            }
        }

        else if (key === "defaultdate") {
            filter.date.default = val;
        }

        else if (key === "numberofpersons") {

            filter.companions.numberOfAdults = +val;
        }
        else if (key === "numberofbabies") {

            filter.companions.numberOfBabies = +val;
        }
        else if (key === "numberofpets") {

            filter.companions.numberOfPets = +val;
        }
        else if (key === "numberofchildren") {

            filter.companions.numberOfChildren = +val;
        }

        // usually we have only one location. but more is possible. in that case we need to get all the values
        // from the definitions.
        else if (key.startsWith("locations[")) {

            // we need to get the index, as that should match with the initLocations..
            const locIndex = + key.replaceAll("locations[", "").replaceAll("]", "");

            // val => string with one or more selections. e.g. 1,130,10 (meaning netherlands, zeeland, burgh haamstede)
            // 1 => nederland
            // 1, 130 => nederland, zeeland
            // 1,-1, 10 => nederland + burghaamstede...(no region selected)

            const locIds = val.split(',').map(t => +t);
            if (locIds.length > 0) {

                const le = new FilterSelectionLocationEntry();
                filter.location.selection.push(le);

                le.countryValueId = locIds[0]; // country
                const countryData = getLocationFromDefinitions(le.countryValueId);
                if (countryData) {
                    le.countryName = countryData.label;
                }

                if (locIds.length > 1) {

                    if (locIds[1] != -1) {  // -1 => skip, no region selected, should have a city selected?

                        le.regionValueId = locIds[1];
                        const regionData = getLocationFromDefinitions(le.regionValueId);
                        if (regionData) {
                            le.regionName = regionData.label;
                        }
                    }
                }

                if (locIds.length > 2) {

                    le.cityValueId = locIds[2];
                    const cityData = getLocationFromDefinitions(le.cityValueId);
                    if (cityData) {
                        le.cityName = cityData.label;
                    }

                }
            }

        }

        // the rest is parsing of the "Is", "Has", hasSearch, "amount",.
        // these all work the same, more or less
        // some are converted to specific properties, such as bedrooms, bathrooms, accommodation type and location.
        else if (key.startsWith("is[")) {
            allIs.push({ key, val });
        }
        else if (key.startsWith("has[")) {
            allHas.push({ key, val });
        }
        else if (key.startsWith("amount[")) {
            allAmount.push({ key, val });
        }
        else if (key.startsWith("hassearch[")) {
            allHasSearch.push({ key, val });
        }

        else if (key === "orderby") {
            filter.orderBy = val;
        }


        // note that  paging data will normally be set through the init settings
        // as this is set server side. therefore we do not need to read it from the url 
        //(since values will be absent in the url, but will be set in from the server )
        //else if (key === "page") { // do not use starts with here, because pageSize and pageCount will also  trigger

        //    paging.setPage(val);
        //}
        //else if (key === "pageCount") {

        //    paging.setPageCount(val);
        //}
        //else if (key === "pageSize") {

        //    paging.setPageSize(val);
        //}

    });


    const isParams = urlConvertersDefaultModelBinder.parse("is", allIs);
    const hasParams = urlConvertersDefaultModelBinder.parse("has", allHas);
    const hasSearchParams = urlConvertersDefaultModelBinder.parse("hassearch", allHasSearch);
    const amountParams = urlConvertersDefaultModelBinder.parse("amount", allAmount);

    // currently we do not use these params directly, but they are converted to specific types.

    // isParams => accommodation type and location

    // try get
    const hasAccType = isParams.filter((v) => v.paramId === searchComposables.getFilterDefinitions().amenityIdAccommodationType).flatMap(t => t.values);
    if (hasAccType.length > 0) {
        filter.accommodationTypes.push(...hasAccType);
        filter.accommodationTypeNoPreference = false;
    } else {
        filter.accommodationTypeNoPreference = true;
    }

    // location is now a seperate property on the search fields.

    //// currently we have no way to define if a isParam is used for a location, since we do not specify those definitions
    //// for now: anything that is not accommodation type is a location. if we have more Is definitiions in the future,
    //// we either need to check those as well, or move the location to a specific property for the querystring..
    //const locationSelection = isParams.find(v => ![definitions.amenityIdAccommodationType /* , add any other definitions to filter.. */].some(d => d == v.paramId));
    //if (locationSelection) {

    //    filter.location.amenityId = locationSelection.paramId;
    //    filter.location.valueId = locationSelection.values[0];

    //}

    // amountParams => bedrooms, bathrooms,
    const bedroomSelection = amountParams.find((v) => v.paramId === searchComposables.getFilterDefinitions().amenityIdBedrooms);
    if (bedroomSelection) {

        filter.bedrooms = bedroomSelection.minValue ?? 1;
    }

    const bathroomSelection = amountParams.find((v) => v.paramId === searchComposables.getFilterDefinitions().amenityIdBathrooms);
    if (bathroomSelection) {

        filter.bathrooms = bathroomSelection.minValue ?? 1;
    }



    // hasParams => currently not used since we use search criteria and not amenities directly.


    // hasSearch => criteria
    // note that the criteria is a bit of a special collection, as it contains
    // selected items based on the group to which the criterium belongs.
    // so we need to first check to which criteria group our selected item belongs.

    hasSearchParams.forEach((sp) => {

        // we only need param id here
        // search our search criteria definitions:
        loopGroups:
        for (let groupIndex = 0; groupIndex < searchComposables.getFilterDefinitions().searchCriteriaGrouping.length; groupIndex++) {

            loopCriteria:
            for (let critIndex = 0; critIndex < searchComposables.getFilterDefinitions().searchCriteriaGrouping[groupIndex].items.length; critIndex++) {

                if (searchComposables.getFilterDefinitions().searchCriteriaGrouping[groupIndex].items[critIndex].keyId == sp.paramId) {

                    // add the current search param to:
                    filter.criteria[groupIndex].push(sp.paramId);

                    // break out of group loop, since we found what we need for the current search param.
                    break loopGroups;
                }
            }
        }



    });



    return filter;
}


/**
 * Reads and fills the filter model from either the current search param on the url, or
 * in case of a rewrite, it will use the set initquerystring from the url rewrite parameters 
 * as passed down from the controller.
 * 
 * @param filter
 */
export function fromUrlOrRewrite(filter: FilterModel): FilterModel {

    if (searchComposables.hasSearchMetaDefinitions() && searchComposables.getSearchMetaDefinitions().queryStringFromRewrite) {

        return fromUrlBase(filter, searchComposables.getSearchMetaDefinitions().queryStringFromRewrite);
    }


    return fromUrlBase(filter, window.location.search);



}